import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  TextField,
  Typography,
  IconButton,
  Pagination,
  AppBar,
  Toolbar,
  Badge,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LeftToolbar from "./LeftToolbar";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import AddIcon from '@mui/icons-material/Add';
import { alpha } from "@mui/material/styles";

import Footer from "./Footer";

const MainContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  backgroundColor: "#f5f7fa",
});

const MainContent = styled(Box)(({ theme, $collapsed }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: $collapsed ? "80px" : "280px",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  [theme.breakpoints.down("sm")]: {
    marginLeft: 0,
    padding: theme.spacing(2),
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "8px",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(2),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    "&:first-of-type": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  }));

function Categories() {
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({ categoryName: "" });
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("Publish Module");
  const [searchQuery, setSearchQuery] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          "/api/support/categories"
        );
        setCategories(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleInputChange = (e) => {
    setFormData({ categoryName: e.target.value });
  };

  const handleCreateCategory = async () => {
    if (formData.categoryName) {
      try {
        setLoading(true);
        const { data } = await axios.post(
          "/api/support/categories",
          formData
        );
        setCategories([...categories, data]);
        setFormData({ categoryName: "" });
        handleCloseModal();
      } catch (error) {
        console.error("Error adding category:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const ModernTableCell = styled(TableCell)(({ theme }) => ({
      padding: theme.spacing(2),
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      "&:first-of-type": {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
      },
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(1.5),
      },
    }));

  const filteredCategories = categories.filter(category =>
  category.categoryName.toLowerCase().includes(searchQuery.toLowerCase()) ||
  category.id.toString().includes(searchQuery)
);

  
  return (
    <MainContainer>
       <AppBar
        position="fixed"
        sx={{
          backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 200%)",  
          boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          left: collapsed ? "80px" : "280px",
          width: `calc(100% - ${collapsed ? "80px" : "280px"})`,
          transition: theme.transitions.create(["width", "left"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          [theme.breakpoints.down("sm")]: {
            left: 0,
            width: "100%",
          },
        }}
      >
         <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
           IPPIS SUPPORT PORTAL
          </Typography>
          <IconButton color="inherit">
            <Badge  color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <LeftToolbar 
        selectedMenu={selectedMenu}
        onSelectMenu={setSelectedMenu}
        onCollapse={setCollapsed} />

      <MainContent $collapsed={collapsed}>
        <Toolbar />
        
        <Typography
          variant="h4"
          sx={{
            textAlign: { xs: "center", sm: "left" },
            color: "primary.main",
            fontWeight: 600,
            mb: 3,
            fontSize: { xs: "1.5rem", sm: "2rem" },
          }}
        >
          Category Management
        </Typography>

        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', mb: 2 }}
        >

           <TextField
    variant="outlined"
    size="small"
    placeholder="Search categories..."
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
    sx={{
      width: isMobile ? '100%' : 300,
      '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      }
    }}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon color="action" />
        </InputAdornment>
      ),
      endAdornment: searchQuery && (
        <InputAdornment position="end">
          <IconButton
            size="small"
            onClick={() => setSearchQuery('')}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      )
    }}
  />


          <Button 
             variant="contained" 
        color="primary" 
        onClick={handleOpenModal}
        startIcon={<AddIcon />}
        size={isMobile ? "small" : "medium"}
          >
            {isMobile ? "Add" : "Add Category"}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", height: "200px", alignItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
             <ResponsiveTableContainer
  component={Paper}
  sx={{ width: isMobile ? "100%" : "80%" }}
>
  <Table size={isMobile ? "small" : "medium"}>
    <TableHead>
      <TableRow
        sx={{
          bgcolor: theme.palette.primary.light,
          '& th': {
            color: 'white',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          },
        }}
      >
        <ModernTableCell>ID</ModernTableCell>
        <ModernTableCell>Category Name</ModernTableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {categories.length > 0 ? (
        filteredCategories.length > 0 ? (
          filteredCategories
            .slice((page - 1) * rowsPerPage, page * rowsPerPage)
            .map((category) => (
              <StyledTableRow key={category.id} hover>
                <ModernTableCell>{category.id}</ModernTableCell>
                <ModernTableCell>{category.categoryName}</ModernTableCell>
              </StyledTableRow>
            ))
        ) : (
          <StyledTableRow>
            <ModernTableCell colSpan={2} align="center" sx={{ py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No categories match your search
              </Typography>
            </ModernTableCell>
          </StyledTableRow>
        )
      ) : (
        <StyledTableRow>
          <ModernTableCell colSpan={2} align="center" sx={{ py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No categories found
            </Typography>
          </ModernTableCell>
        </StyledTableRow>
      )}
    </TableBody>
  </Table>
</ResponsiveTableContainer>


            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={Math.ceil(categories.length / rowsPerPage)}
                page={page}
                onChange={handleChangePage}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          </>
        )}
      </MainContent>

      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0, 0, 0, 0.6)" 
        }}
      >
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            borderRadius: 2,
            width: isMobile ? "90%" : 400,
            mx: "auto",
            mt: 5,
            boxShadow: 3,
            position: "relative",
            
          }}
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{ position: "absolute", top: 8, right: 8 }}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
            Add New Category
          </Typography>
          <TextField
            label="Category Name"
            fullWidth
            size="small"
            value={formData.categoryName}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateCategory}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Creating..." : "Create Category"}
            </Button>
          </Box>
        </Box>
      </Modal>
    <Footer collapsed={collapsed} />
    </MainContainer>
  );
}
export default Categories;